import {
  AlloyButton,
  Input,
  Validator,
  ChildModal,
  useModal,
  ComponentLoader
} from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import { Form, Formik, useFormikContext } from 'formik';
import Field from '@app/components/common/form/Field';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ShareCartChildModal from '../../ShareCartChildModal';
import ExceptionFeedbackBySelector from '@app/components/common/Exception/ExceptionFeedbackBySelector';
import WarningTextMessage from '../WarningTextMessage';
import {
  CAMPAIGN_ID_INPUT_NAME,
  CAMPAIGN_ID_INPUT_MAX_LIMIT,
  PROMO_CODE_INPUT_NAME,
  PROMO_CODE_INPUT_MAX_LIMIT,
  PCC_SHOPPING_CART
} from '../../../constants';
import cx from 'classnames';
import { buildShareCartLink } from '@app/store/checkout/actions';
import { useSystemFeedback } from '@app/hooks';
import { STATUS } from '@app/constants/commonConstants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import styles from '../ShareCartCILGModal.module.scss';

const DealerShareCartModal = ({ isOpen, closeModal, errorSelector }) => {
  const [t] = useTranslation();
  const initialValues = {
    campaignId: '',
    promoCode: ''
  };
  const { values } = useFormikContext();
  const dispatch = useDispatch();
  const { setError, clearError } = useSystemFeedback();
  const shareCartStatus = useSelector(s => s.checkout?.shareCartStatus);
  const shareCartLoader = shareCartStatus === STATUS.PENDING;

  const error = t('THIS_FIELD_REQUIRED');
  const cancelMergeModalProps = useModal({
    isChild: true,
    parentIsOpen: isOpen
  });

  const campaignIdValidator = val =>
    new Validator(val).required(error).getError();

  const handleSubmit = (val, { resetForm }) => {
    const requestBody = {
      appName: PCC_SHOPPING_CART,
      equipment: values?.equipment,
      ...val
    };
    const message = { message: t('UNKNOWN_ERROR_MESSAGE') };
    dispatch(buildShareCartLink(requestBody, setError, message)).then(res => {
      if (res) {
        clearError(ERROR_DOMAIN.CHECKOUT, ERROR_PATH.SHARE_CART_MODAL);
        cancelMergeModalProps.open();
        resetForm();
      }
    });
  };

  return (
    <>
      {shareCartLoader && <ComponentLoader />}
      <ExceptionFeedbackBySelector
        selector={errorSelector}
        skipTranslation
        className="mb-2"
        showReferenceId
      />
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        <Form
          className={cx(
            'd-flex flex-column',
            styles['share-cart-modal__form-container']
          )}
        >
          <div
            className={`${styles['share-cart-modal__form-field']} mb-auto mb-lg-0`}
          >
            <Field
              as={Input}
              name={CAMPAIGN_ID_INPUT_NAME}
              label={t('CAMPAIGN_ID_PROMO_CODE')}
              maxLength={CAMPAIGN_ID_INPUT_MAX_LIMIT}
              showRequired
              className={`mb-1`}
              validate={campaignIdValidator}
              validator={campaignIdValidator}
            />
            <WarningTextMessage />
            <Field
              as={Input}
              name={PROMO_CODE_INPUT_NAME}
              label={t('1480_PROMO_CODE_NEW_LABEL')}
              maxLength={PROMO_CODE_INPUT_MAX_LIMIT}
              showOptional
            />
          </div>
          <div className="d-flex flex-column flex-lg-row-reverse">
            <AlloyButton
              className="mb-lg-0 mb-1"
              buttonType="primary"
              type="submit"
            >
              {t('GET_LINK')}
            </AlloyButton>
            <AlloyButton
              className="m-lg-0 mt-2"
              buttonType="ghost"
              onClick={closeModal}
            >
              {t('MO_CancelButton')}
            </AlloyButton>
          </div>
        </Form>
      </Formik>
      <ChildModal
        title={t('SHARE_CART')}
        backButtonMessage={t('BACK')}
        {...cancelMergeModalProps}
        className={cx('d-flex flex-column', styles['share-cart-modal'])}
      >
        <ShareCartChildModal
          childClose={cancelMergeModalProps.close}
          close={closeModal}
        />
      </ChildModal>
    </>
  );
};

DealerShareCartModal.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  errorSelector: PropTypes.func
};

export default DealerShareCartModal;
